import * as T from 'three';
import { sampleSurface } from '../geometry/SurfaceSampler';
import { vertexShader, fragmentShader, ambientVertex, ambientFragment } from './ParticleShaders';
export const AMBIENT_CAPACITY = 1600;
export class ParticleEngine {
    points: T.Points;
    ambient: T.Points;
    samples: ReturnType<typeof sampleSurface>;
    uniforms: any;
    ambientUniforms: any;
    capacity: number;
    private focusTarget = new T.Vector3();
    constructor(mesh: T.Mesh, count: number) {
        this.capacity = count;
        this.samples = sampleSurface(mesh.geometry, count, mesh.userData.bodyCount, mesh.userData.eyeCenters, mesh.userData.facialFeatures);
        const s = this.samples, g = new T.BufferGeometry();
        g.setAttribute('position', new T.BufferAttribute(s.position, 3));
        g.setAttribute('normal', new T.BufferAttribute(s.normal, 3));
        g.setAttribute('aIndex', new T.BufferAttribute(s.ids, 1));
        g.setAttribute('aSeed', new T.BufferAttribute(s.seed, 1));
        g.setAttribute('aTone', new T.BufferAttribute(s.tone, 1));
        g.setAttribute('aDetail',new T.BufferAttribute(s.detail,1));
        this.uniforms = { uMorph: { value: s.texture }, uAtlas: { value: new T.Vector3(s.width, s.height, s.rows) }, uWeights: { value: new Float32Array(13) }, uTime: { value: 0 }, uMotion: { value: 1 }, uAssembly: { value: 0 }, uDpr: { value: 1 }, uScale: { value: 1 }, uDevicePointSize: { value: 1 }, uSpeaking: { value: 0 }, uDebug: { value: 0 }, uColor: { value: new T.Color('#1555A4') }, uHighlight: { value: new T.Color('#5AA9F2') }, uBrightness: { value: 1.2 } };
        this.uniforms.uFocus = { value: new T.Vector3(0, 0, 1.4) };
        this.uniforms.uFocusStrength = { value: 0 };
        g.setDrawRange(0, count);
        this.points = new T.Points(g, new T.ShaderMaterial({ vertexShader, fragmentShader, uniforms: this.uniforms, transparent: true, depthWrite: false, depthTest: true, blending: T.NormalBlending }));
        this.points.renderOrder = 2;
        this.points.frustumCulled = false;
        const ag = new T.BufferGeometry(), pos = new Float32Array(AMBIENT_CAPACITY * 3), seed = new Float32Array(AMBIENT_CAPACITY);
        let state = 819;
        const rnd = () => { state = Math.imul(state, 1664525) + 1013904223 | 0; return (state >>> 0) / 4294967296; };
        // A volume surrounding the entire head, rather than a cylindrical ring.
        // Prefixes stay uniformly distributed when mobile or frame-time LOD reduces count.
        for (let i = 0; i < AMBIENT_CAPACITY; i++) {
            const a = rnd() * Math.PI * 2, y = rnd() * 2 - 1;
            const radial = Math.sqrt(1 - y * y), shell = 1.02 + rnd() * .32;
            pos.set([Math.sin(a) * radial * 1.08 * shell, y * 1.38 * shell, Math.cos(a) * radial * 1.02 * shell], i * 3);
            seed[i] = rnd();
        }
        ag.setAttribute('position', new T.BufferAttribute(pos, 3));
        ag.setAttribute('aSeed', new T.BufferAttribute(seed, 1));
        ag.setDrawRange(0, count >= 30000 ? 1200 : count >= 20000 ? 720 : 480);
        this.ambientUniforms = { uTime: { value: 0 }, uMotion: { value: 1 }, uDpr: { value: 1 }, uColor: { value: new T.Color('#2479CC') } };
        this.ambientUniforms.uFocus = this.uniforms.uFocus;
        this.ambientUniforms.uFocusStrength = this.uniforms.uFocusStrength;
        this.ambientUniforms.uScale = { value: 1 };
        this.ambientUniforms.uAssembly = this.uniforms.uAssembly;
        this.ambientUniforms.uBrightness = this.uniforms.uBrightness;
        this.ambient = new T.Points(ag, new T.ShaderMaterial({ vertexShader: ambientVertex, fragmentShader: ambientFragment, uniforms: this.ambientUniforms, transparent: true, depthWrite: false, depthTest: true }));
        this.ambient.renderOrder = 3;
        this.ambient.frustumCulled = false;
    }
    update(t: number, weights: Float32Array, assembly: number, speaking: number) { this.uniforms.uTime.value = t; this.uniforms.uWeights.value = weights; this.uniforms.uAssembly.value = assembly; this.uniforms.uSpeaking.value = speaking; this.ambientUniforms.uTime.value = t; }
    focus(x: number, y: number, active: boolean, dt: number) {
        const blend = 1 - Math.exp(-dt * 9);
        this.focusTarget.set(T.MathUtils.clamp(x, -1, 1) * 1.1, T.MathUtils.clamp(y, -1, 1) * 1.25, 1.4);
        this.uniforms.uFocus.value.lerp(this.focusTarget, blend);
        const strength = this.uniforms.uFocusStrength;
        strength.value += ((active ? 1 : 0) - strength.value) * blend;
    }
    density(v: number) { this.points.geometry.setDrawRange(0, Math.max(1000, Math.floor(this.capacity * T.MathUtils.clamp(v, .1, 1)))); }
    motion(v: number) { this.uniforms.uMotion.value = v; this.ambientUniforms.uMotion.value = v; }
    color(hex: string) { this.uniforms.uColor.value.set(hex); this.uniforms.uHighlight.value.copy(this.uniforms.uColor.value).lerp(new T.Color('#5AA9F2'), .6); this.ambientUniforms.uColor.value.copy(this.uniforms.uColor.value).lerp(this.uniforms.uHighlight.value, .5); }
    theme(theme: string) { this.color(theme === 'light' ? '#0A2A58' : '#1555A4'); this.uniforms.uHighlight.value.set(theme === 'light' ? '#2479CC' : '#5AA9F2'); this.ambientUniforms.uColor.value.copy(this.uniforms.uColor.value).lerp(this.uniforms.uHighlight.value, .5); }
    dispose() { this.samples.texture.dispose(); for (const p of [this.points, this.ambient]) {
        p.geometry.dispose();
        (p.material as T.Material).dispose();
    } }
}
